/* Single authority for the CONTENT of the buy button's price pill.
 *
 * Three separate writers touch that node, on three different schedules:
 *
 *   1. hc-product-variant-picker-v2 `_updateAddToCartButton` does
 *      `btnText.textContent = addStr`, which destroys the pill outright — and
 *      `_applyVariant` replays it at +100ms, +500ms and +1500ms, so anything
 *      that merely "writes after a variant change" is overwritten up to a
 *      second and a half later.
 *   2. Broadcast's own label writer rebuilds the pill from `variant.price`,
 *      i.e. the UNDISCOUNTED figure. It is armed by the theme setting this
 *      feature switches on, so enabling the pill also enables its corruption.
 *   3. afroyla-preorder.js `relabel()` owns the VERB and deliberately keeps the
 *      pill intact. It cooperates; nothing here fights it.
 *
 * So this does not race. It states the desired content and converges the DOM
 * to it via a MutationObserver, treating both "pill missing" and "pill shows
 * the wrong figure" as violations. No timing assumptions anywhere.
 *
 * Termination: converge writes only when the text differs. Our own write
 * triggers one more observer pass, which finds the DOM already correct and
 * writes nothing, so the loop closes after a single correction.
 *
 * Verb ownership stays elsewhere — this touches `.btn__price` and nothing else. */
(function () {
  'use strict';

  var BTN = '[data-add-to-cart]';
  var TEXT = '[data-add-to-cart-text]';
  var PILL = 'btn__price';
  var STRIP = '[data-coupon-strip]';

  function formatMoney(cents) {
    if (window.theme && typeof window.theme.formatMoney === 'function') {
      return window.theme.formatMoney(cents, window.theme.moneyFormat);
    }
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* A quick-add modal puts a second buy box on the page, so never reach past
   * the section that owns this button. */
  function scopeOf(node) {
    return (
      (node.closest && (node.closest('.shopify-section') || node.closest('[data-section-id]'))) ||
      document
    );
  }

  function isClaimed(strip) {
    if (strip.getAttribute('data-state') === 'claimed') return true;
    var code = strip.getAttribute('data-coupon-code');
    if (!code) return false;
    try {
      return window.localStorage.getItem('afroyla:coupon:' + code) === '1';
    } catch (e) {
      return false;
    }
  }

  /* Mirrors the Liquid and the strip's JS exactly: floor the saving, then
   * subtract. 13195 − floor(13195 × 50 / 100) = 13195 − 6597 = 6598. Computing
   * the payable directly would give 6597, a cent under what Shopify charges. */
  function savingsFor(priceCents, strip) {
    var fixed = parseInt(strip.getAttribute('data-fixed-cents') || '0', 10);
    var pct = parseFloat(strip.getAttribute('data-discount-pct') || '0');
    var saving = 0;
    if (fixed > 0) {
      saving = fixed;
    } else if (pct > 0) {
      saving = Math.floor((priceCents * pct) / 100);
    }
    return Math.min(saving, priceCents);
  }

  function desiredMoney(btn) {
    /* A selected set overrides the single-variant figures. The set picker owns
     * the math (per-item floor, then sum — flooring the summed total would
     * understate the charge by a cent); this module just renders whichever
     * figure the coupon state calls for. */
    var setBase = parseInt(btn.getAttribute('data-set-base-cents') || '0', 10);
    if (setBase > 0) {
      var setPayable = parseInt(btn.getAttribute('data-set-payable-cents') || '0', 10);
      var setStrip = scopeOf(btn).querySelector(STRIP);
      var cents = setStrip && isClaimed(setStrip) && setPayable > 0 ? setPayable : setBase;
      return formatMoney(cents);
    }

    var price = parseInt(btn.getAttribute('data-price-cents') || '0', 10);
    if (!price) return null;
    var strip = scopeOf(btn).querySelector(STRIP);
    if (strip && isClaimed(strip)) price -= savingsFor(price, strip);
    return formatMoney(price);
  }

  /* The compare-at strike is the theme's sale indicator and has nothing to do
   * with the coupon, so read and write around it rather than through it. */
  function pillMoney(pill) {
    var text = '';
    for (var node = pill.firstChild; node; node = node.nextSibling) {
      if (node.nodeName === 'S') break;
      if (node.nodeType === 3) text += node.nodeValue;
    }
    return text.trim();
  }

  function writePill(pill, money) {
    var strike = pill.querySelector('s');
    var kept = strike ? strike.cloneNode(true) : null;
    pill.textContent = money;
    if (kept) {
      pill.appendChild(document.createTextNode(' '));
      pill.appendChild(kept);
    }
  }

  function converge(btn) {
    // Sold out: the theme disables the button and the gold CSS hides the pill.
    // Leave the DOM alone rather than resurrecting a price nobody can pay.
    if (btn.disabled) return;

    var text = btn.querySelector(TEXT);
    if (!text) return;

    var money = desiredMoney(btn);
    if (money === null) return;

    var pill = text.querySelector('.' + PILL);
    if (!pill) {
      pill = document.createElement('span');
      pill.className = PILL;
      text.appendChild(document.createTextNode(' '));
      text.appendChild(pill);
    }

    if (pillMoney(pill) !== money) writePill(pill, money);
  }

  function observe(btn) {
    if (btn.__afroylaPill) return;
    var text = btn.querySelector(TEXT);
    if (!text) return;
    btn.__afroylaPill = true;

    converge(btn);

    if (!window.MutationObserver) return;
    new MutationObserver(function () {
      converge(btn);
    }).observe(text, { childList: true, subtree: true, characterData: true });
  }

  function buttons(root) {
    return (root || document).querySelectorAll(BTN);
  }

  function adopt(root) {
    var list = buttons(root);
    for (var i = 0; i < list.length; i++) observe(list[i]);
  }

  function convergeAll() {
    var list = buttons(document);
    for (var i = 0; i < list.length; i++) converge(list[i]);
  }

  /* The picker updates the hidden id input before dispatching, so the new price
   * is already authoritative here; the observer separately mops up the delayed
   * replays that follow. */
  document.addEventListener('theme:variant:change', function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant || typeof variant.price !== 'number') return;
    var list = scopeOf(event.target).querySelectorAll(BTN);
    for (var i = 0; i < list.length; i++) {
      list[i].setAttribute('data-price-cents', variant.price);
      converge(list[i]);
    }
  });

  document.addEventListener('afroyla:coupon:claimed', convergeAll);
  document.addEventListener('afroyla:set:price', convergeAll);

  /* Unclaiming needs the same nudge as claiming. The observer only watches the
   * button's own text, so when a code leaves the order nothing rewrites the
   * pill and it keeps quoting the discount the shopper no longer has — while
   * every other surface has already gone back to full price. */
  document.addEventListener('afroyla:discount:removed', convergeAll);
  document.addEventListener('afroyla:discount:applied', convergeAll);

  adopt(document);

  // Quick-add modals and carousels inject buy boxes long after load.
  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(BTN)) observe(node);
          else if (node.querySelector && node.querySelector(BTN)) adopt(node);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
